import {ILastDatasBuilder, JLastDatas} from "/@lib@/commons/lastDatas.js";
import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {BaseAreaView, OBaseAreaViewInit} from "/@lib@/commons/views.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IWspUiEnv, JWspUriChangeMsg, Wsp} from "/@lib@/wsp/wsp.js";
import {EWspChangesEvts, WspsLivePlace} from "/@lib@/wsp/wspsLive.js";
import {isItemUiEnv} from "/@back@/wsp/views/itemMain.js";
import {ESrcField, JSrcFields, SRC, srcUri} from "/@lib@/wsp/src.js";
import {IO} from "/@lib@/commons/io/io.js";
import {EInvolvement, ETaskStage, TASK} from "/@lib@/wsp/lcTask.js";
import {CDM} from "/@lib@/commons/utils/cdm.js";
import {Label} from "/@back@/commons/basis.js";
import {GridColDef, IGridLineDrawer} from "/@back@/commons/widgets/grid-core.js";
import {CellBuilderItModelFlagIcon, CellBuilderLc, CellBuilderSrcIconCode, CellBuilderTkDeadline} from "/@back@/wsp/widgets/srcGridColumns.js";
import {OSrcGridInit, SrcGrid} from "/@back@/wsp/widgets/srcGrid.js";
import {JUser} from "/@lib@/core/user.js";
import {GridDataRowJson} from "/@back@/commons/widgets/grid-libs.js";
import {AccelKeyMgr} from "/@lib@/commons/actions.js";
import {Button, OButtonActionsInit} from "/@back@/commons/widgets/buttons.js";
import {EItemTypeFamily, ItemTypeTask, LC_DEFAULT_NAME} from "/@lib@/wsp/wspMetaUi.js";
import {CreateTask} from "/@back@/wsp/actions/lcTaskActions.js";
import {IShortDescCtx} from "/@lib@/wsp/item.js";
import "/@back@/wsp/views/views_Perms.js";
import {FocusTask} from "/@back@/wsp/actions/taskActions.js";
import IS_element = DOM.IS_element;


/**
 */
export interface Tasks extends BaseAreaView<IRegPointer<IWspUiEnv>> {
	initialize(init: OTasksInit): this
}

export interface OTasksInit extends OBaseAreaViewInit<IRegPointer<IWspUiEnv>> {
	/** Si affiché en tooltip, pas d'abonnements */
	ephemeralView?: boolean
	/** Notifie à chaque rechargement d'une liste (rafraichit les compteurs dans le ribbon) */
	refreshCb?: (visibleStage: ETaskStage, tasks: JSrcFields[]) => void
	lastDatas?: JLDTasks
}

interface JLDTasks {
	tab: 'do'/* to do */ | 'fo'/* to follow */ | 'pe' /* pending */ | 'co' /* completed */ | 'fc'/* forthcoming */
}

export class Tasks extends BaseAreaView<IRegPointer<IWspUiEnv>> implements ILastDatasBuilder, IGridLineDrawer {
	reg: IReg<IWspUiEnv>;
	wsp: Wsp;

	ephemeralView?: boolean;

	refreshCb: (visibleStage: ETaskStage, tasks: JSrcFields[]) => void;


	get taskScope(): 'my' | 'item' {return isItemUiEnv(this.reg.env) ? 'item' : 'my'}

	get itemRef(): srcUri {return isItemUiEnv(this.reg.env) ? SRC.srcRef(this.reg.env.longDesc) : null}

	get itemLongDesc(): JSrcFields {return isItemUiEnv(this.reg.env) ? this.reg.env.longDesc : null}

	get currentUser(): JUser {return this.reg.env.universe.auth.currentUser}

	get visibleStage(): ETaskStage {
		switch (this._currentTab.id) {
		case "do":
		case "fo":
		case "pe":
			return ETaskStage.pending;
		case "co":
			return ETaskStage.completed;
		case "fc":
			return ETaskStage.forthcoming;
		}
	}

	protected _currentTab: Label;

	protected _tasksFields: string;

	protected _grid: SrcGrid;

	protected _fetchingMsg: string;


	protected _initialize(init: OTasksInit) {
		super._initialize(init); //init this.reg, this.area, this.areaContext
		this.wsp = this.reg.env.wsp;
		this.ephemeralView = init.ephemeralView;
		this.refreshCb = init.refreshCb;

		const taskFields: ESrcField[] = ["srcUri", "srcId", "itSgn", "itModel", "actTi", "srcDt", "srcUser", "lcSt", "lcDt", "lcBy", "rspUsrs", "actCts", "actStage", "tkDeadline", "srcRoles", "srcRi"]; //todo configurable ?
		this._tasksFields = taskFields.join('*');

		//Construction Ui
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin('c-tabs/base', init);
		this.reg.installSkin('c-tabs', sr);
		this.reg.installSkin('scroll/small', sr);
		this.reg.installSkin(this.localName, sr);
		const allTabs = sr.appendChild(<div id="allTabs"/>);

		allTabs.appendChild(<c-button-actions id="createTask" î={{
			reg: this.reg,
			icon: "/@skin@/wsp/views/tasks/add.svg",
			title: "Créer une nouvelle tâche",
			actionContext: {
				reg: this.reg,
				shortDescs: this.itemLongDesc ? [this.itemLongDesc] : [],
				infoBroker: this.reg.env.infoBroker,
				emitter: this,
			} as IShortDescCtx,
			actions: this.reg.env.wsp.wspMetaUi.getItemTypes().filter((t) => t instanceof ItemTypeTask).map((t) => new CreateTask<IShortDescCtx>(t as ItemTypeTask))
		} as OButtonActionsInit<IShortDescCtx>}/>);

		allTabs.onclick = (ev: MouseEvent) => {
			if (ev.target instanceof Button && ev.target.classList.contains("tab")) {
				this.setCurrentTab(ev.target);
				this.fetchTasks();
			}
		};
		switch (this.taskScope) {
		case "my":
			allTabs.append(<div id="tabs">
					<c-button id="do" class="tab" label="À faire" title="Tâches à faire"/>
					<c-button id="fo" class="tab" label="À suivre" title="Tâches à suivre"/>
				</div>,
				<div class="flex"/>,
				<div id="tabs">
					{this.wsp.wspMetaUi.hasForthcomingTasks() ? <c-button id="fc" class="tab" label="À venir" title="Tâches à venir"/> : undefined}
					<c-button id="co" class="tab" label="Closes" title="Tâches closes"/>
				</div>
			);
			break;
		case "item":
			allTabs.append(<div id="tabs">
				<c-button id="pe" class="tab" label="En cours" title="Tâches en cours"/>
				{this.wsp.wspMetaUi.hasForthcomingTasks() ? <c-button id="fc" class="tab" label="À venir" title="Tâches à venir"/> : undefined}
				<c-button id="co" class="tab" label="Closes" title="Tâches closes"/>
			</div>);
			break;
		default:
			throw Error("taskScope unknown " + this.taskScope);
		}

		// colonnes
		this.reg.addToList("columns:tasksview", "type", 1, new GridColDef('type').setLabel("")
			.setMinWidth('1.5em').setMaxWidth('1.5em').setSortable(true)
			.setCellBuilder(new CellBuilderItModelFlagIcon(this.reg, this.wsp.wspMetaUi))
			.setSkin("wsp-tasks/grid"), 0);
		this.reg.addToList("columns:tasksview", "label", 1, new GridColDef('label').setLabel("Libellé")
			.setFlex('10em', 1, 1).setMinWidth('5em').setSortable(true)
			.setCellBuilder(new CellBuilderSrcIconCode(this.reg, this.wsp.wspMetaUi, false).setIconWidth('0').override('_getIcon', () => "")), 0);
		this.reg.addToList("columns:tasksview", "lc", 1, new GridColDef('lc').setLabel(LC_DEFAULT_NAME)
			.setFlex('4em', 1, 1).setMinWidth('2em').setSortable(true)
			.setCellBuilder(new CellBuilderLc(this.wsp.wspMetaUi)), 0);
		this.reg.addToList("columns:tasksview", "deadline", 1, new GridColDef('deadline').setLabel("Date limite")
			.setFlex('5em', 1, 0).setMinWidth('4em').setSortable(true)
			.setCellBuilder(new CellBuilderTkDeadline()), 0);

		//grid
		const gridInit: OSrcGridInit = {
			reg: this.reg,
			columnDefs: this.reg.mergeLists("columns:tasksview", "columns:tasksview:" + this.id),
			emptyBody: () => {
				return <c-msg label={this._fetchingMsg || "Aucune tâche trouvée"}/>;
			},
			itemHandlingReact: this.reg.env.infoBroker,
			onWspUriChangeScope: "longDesc",
			actions: this.reg.mergeLists("actions:wsp:shortDesc:task"),
			accelKeyMgr: new AccelKeyMgr().initFromMapActions(this.reg.mergeListsAsMap("accelkeys:wsp:shortDesc:task")),
			defaultAction: new FocusTask(),
			lineDrawer: this,
			skinScroll: 'scroll/small',
			autoSelOnFocus: "first"
		};
		this._grid = sr.appendChild(<wsp-src-grid î={gridInit}/>) as SrcGrid;

		//init tab.
		const tab = init.lastDatas ? init.lastDatas.tab : null;
		this.setCurrentTab((tab ? sr.getElementById(tab) as Button : null) || DOM.findNext(allTabs, allTabs, (n: Node): n is Button => IS_element(n) && n.classList.contains("tab")));
	}

	protected _refresh() {
		super._refresh();
		const sr = this.shadowRoot;
		DOM.setAttrBool(sr.getElementById("createTask"), "disabled", !this.reg.hasPerm("action.tasks#create.task"));
	}

	setCurrentTab(tab: Button) {
		if (this._currentTab) this._currentTab.classList.toggle("selected", false);
		this._currentTab = tab;
		tab.classList.toggle("selected", true);
	}

	/** rétention locale de cette info qui nécessite une requête dédiée (et pas anodine en temps de traitement),
	 * et qui évolue suffisamment peu pour considérer que c'est une constante entre deux fetch globaux */
	private _lastUserFlatGroups: string[];

	/** Refresh complet de la liste des tasks */
	async fetchTasks() {
		this.showTasksList(null, 'loading');
		try {
			const currTab = this._currentTab;
			const qs = ["cdaction", "FindTasks", "param", this.wsp.code, "fields", this._tasksFields] as any[];
			let options: any;
			const user = this.currentUser;
			this._lastUserFlatGroups = await this.reg.env.universe.auth.fetchFlattenedGroups() || [user.account];
			if (currTab !== this._currentTab || user !== this.currentUser) return; //race cond

			switch (this.taskScope) {
			case "my":
				qs.push("refUriFrom", ""); //tout l'atelier
				options = {includeDescendants: true, involvedUser: user.account, involvedUserGroups: true};
				break;
			case "item":
				options = {};
				qs.push("refUriFrom", this.itemRef);
				break;
			default:
				throw Error("taskScope unknown " + this.taskScope);
			}
			const visibleStage = this.visibleStage;
			if (visibleStage !== ETaskStage.pending) options.actStage = visibleStage;
			qs.push("options", CDM.stringify(options));

			const tasks = await this.reg.env.universe.wspServer.config.tasksUrl.fetchJson<JSrcFields[]>(IO.qs(...qs));
			if (currTab !== this._currentTab || user !== this.currentUser) return; //race cond
			if (this.refreshCb) this.refreshCb(visibleStage, tasks);

			//Filtre
			for (let i = tasks.length - 1; i >= 0; i--)
				if (!Tasks.taskIsShown(this._currentTab.id, this.taskScope, tasks[i], this._lastUserFlatGroups, this.wsp)) tasks.splice(i, 1);

			this.showTasksList(tasks);
		} catch (e) {
			this.showTasksList(null, 'error');
		}
	}

	private showTasksList(tasks: JSrcFields[]|null, state:'error'|'loading'|null=null){
		if(state==='error')
			this._fetchingMsg="Chargement en cours...";
		else if(state==='loading')
			this._fetchingMsg="Chargement en cours...";
		else this._fetchingMsg = null;

		this._grid.dataHolder.setDatas(tasks||[]);
	}

	/** Retourne true si la tache doit être affichée dans ce contexte */
	private static taskIsShown(currentTabId: string, taskScope: string, task: JSrcFields, userFlatGroups: string[], wsp: Wsp): boolean {
		const inv: EInvolvement = currentTabId === 'do' ? EInvolvement.executor : currentTabId === 'fo' ? EInvolvement.follower : null;
		if (inv) {
			return ((TASK.getInvolvementForAccounts(taskScope === 'my' ? userFlatGroups : '*', task, wsp.wspMetaUi) & inv) > 0);
		}
		return true;
	}

	protected shown: boolean;

	onViewShown() {
		if (!this.ephemeralView && !this._onConnRenewed) {
			if (this.taskScope === "my") {
				this._wspUriChange = this.onWspUriChange.bind(this);
				this.reg.env.place.eventsMgr.on("wspUriChange", this._wspUriChange);
			}
			this._onConnRenewed = this.onConnRenewed.bind(this);
			this.reg.env.place.eventsMgr.on("onConnectionRenewed", this._onConnRenewed);
		}
		this.shown = true;
		this.fetchTasks();
	}

	onViewHidden(closed?: boolean): void {
		this.shown = false;
		if (closed) {
			if (this._wspUriChange) {
				this.reg.env.place.eventsMgr.removeListener("wspUriChange", this._wspUriChange);
				this._wspUriChange = null;
			}
			if (this._onConnRenewed) {
				this.reg.env.place.eventsMgr.removeListener("onConnectionRenewed", this._onConnRenewed);
				this._onConnRenewed = null;
			}
		}
	}

	buildLastDatas(parentLastDatas: JLastDatas) {
		const lastDatasKey = this.getAttribute('last-datas');
		if (lastDatasKey) {
			parentLastDatas[lastDatasKey] = {tab: this._currentTab.id as any} as JLDTasks;
		}
	}

	redrawLine(row: GridDataRowJson<JSrcFields>, line: HTMLElement): void {
		const dt = row.rowDatas.tkDeadline;
		line.classList.toggle("late", dt > 0 && dt < Date.now());
		line.classList.toggle("comleted", row.rowDatas.actStage === "completed");
		// Utile pour les évolutons des états *sans* fetch global de la view
		line.classList.toggle("isNotShown", !Tasks.taskIsShown(this._currentTab.id, this.taskScope, row.rowDatas, this._lastUserFlatGroups, this.wsp));
		this._grid.redrawLine(row, line);
	}

	protected _wspUriChange: (msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') => void;

	protected onWspUriChange(msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') {
		if (msg.involved && msg.type === EWspChangesEvts.u) {
			//La src de cette modif implique le user connecté
			const type = this.wsp.wspMetaUi.getItemType(msg.itModel);
			if (type.getFamily() === EItemTypeFamily.task) {
				//C'est une task, si pas dans la liste faut rafraichir.
				if (!this._grid.srcGridDatas.findRowKeyBySrcId(msg.srcId)) this.fetchTasks();
			}
		}
	}

	protected _onConnRenewed: () => void;

	protected async onConnRenewed() {
		if (this.shown) setTimeout(() => {if (this.shown) this.fetchTasks()}, 200);
	}

}

REG.reg.registerSkin("wsp-tasks", 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	#tabs {
		display: contents;
	}

	#allTabs {
		display: flex;
		border-bottom: 1px solid var(--border-color);
	}

	#createTask[disabled] {
		display: none;
	}

	.tab {
		flex: 1 1 auto;
		min-width: 2em !important;
		cursor: pointer;
		border: unset;
		border-radius: 0;
	}

	.flex {
		flex: 1;
		display: flex;
	}

	wsp-src-grid {
		flex: 1;
		border: none;
	}
`);

REG.reg.registerSkin("wsp-tasks/grid", 1, /* language=CSS */ `
	.icon {
		padding-inline-start: 1.5em;
	}

	.late {
		color: var(--error-color);
	}

	.comleted {
		text-decoration-line: line-through;
	}

	.isNotShown {
		font-style: italic;
		opacity: .8;
	}
`);
customElements.define("wsp-tasks", Tasks);
